import { Collection, getFirebase } from 'firestorter';
import CollectionName from '../utils/collections';
import BaseDocument from './BaseDocument';

/**
 * ChatDraftMessage class.
 * Each draft message saved in a chat typing area is an instance of this class.
 * @class ChatDraftMessage
 * @extends BaseDocument
 */
class ChatDraftMessage extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.CHAT_DRAFT_MESSAGE}/${id}`, opts);
  }

  /**
   * Get saved draft message text.
   * @return {string}
   */
  get text() {
    return this.data.text;
  }

  /**
   * userId the unread count is for.
   * @return {string}
   */
  get userId() {
    return this.data.userId;
  }

  /**
   * channelId the unread count is for.
   * @return {string}
   */
  get channelId() {
    return this.data.channelId;
  }

  /**
   * The time of when draft was saved
   * @return {Date}
   */
  get savedAt() {
    return this.data.savedAt?.toDate();
  }

  /**
   * Set saved draft message text.
   * @return Promise<void>
   */
  setText(text) {
    return this.updateFields({
      text,
      savedAt: new Date(),
    });
  }

  /**
   * Method to get particular message
   * @return Promise<>
   */
  static async getMessageDoc(channelId, userId) {
    const messageSnapshot = new Collection(() => `/${CollectionName.CHAT_DRAFT_MESSAGE}`, {
      createDocument: ({ id }, opts) => new ChatDraftMessage(id, opts),
      query: (ref) => ref
        .where('userId', '==', userId)
        .where('channelId', '==', channelId)
        .limit(1),
    });

    await messageSnapshot.fetch();

    if (messageSnapshot.docs.length > 0) {
      return messageSnapshot.docs[0];
    }

    const db = getFirebase().default.firestore();
    const newDraftMessageDocRef = db.collection(CollectionName.CHAT_DRAFT_MESSAGE).doc();

    await db.runTransaction(async (transaction) => transaction.set(newDraftMessageDocRef, {
      userId,
      channelId,
      text: '',
      savedAt: new Date(),
    }));

    return new ChatDraftMessage(newDraftMessageDocRef.id);
  }
}

export default ChatDraftMessage;
